import { NodeMaterialBlockConnectionPointTypes } from "./Enums/nodeMaterialBlockConnectionPointTypes.js";
import { NodeMaterialConnectionPoint, NodeMaterialConnectionPointDirection } from "./nodeMaterialBlockConnectionPoint.js";
import { NodeMaterialBlockTargets } from "./Enums/nodeMaterialBlockTargets.js";
import { UniqueIdGenerator } from "../../Misc/uniqueIdGenerator.js";
import { GetClass } from "../../Misc/typeStore.js";
/**
 * Defines a block that can be used inside a node based material
 */
var NodeMaterialBlock = /** @class */ (function () {
    /**
     * Creates a new NodeMaterialBlock
     * @param name defines the block name
     * @param target defines the target of that block (Vertex by default)
     * @param isFinalMerger defines a boolean indicating that this block is an end block (e.g. it is generating a system value). Default is false
     * @param isInput defines a boolean indicating that this block is an input (e.g. it sends data to the shader). Default is false
     */
    function NodeMaterialBlock(name, target, isFinalMerger, isInput) {
        if (target === void 0) { target = NodeMaterialBlockTargets.Vertex; }
        if (isFinalMerger === void 0) { isFinalMerger = false; }
        if (isInput === void 0) { isInput = false; }
        this._isFinalMerger = false;
        this._isInput = false;
        this._name = "";
        this._isUnique = false;
        /** Gets or sets a boolean indicating that only one input can be connected at a time */
        this.inputsAreExclusive = false;
        /** @hidden */
        this._codeVariableName = "";
        /** @hidden */
        this._inputs = new Array();
        /** @hidden */
        this._outputs = new Array();
        /**
         * Gets or sets the comments associated with this block
         */
        this.comments = "";
        /** Gets or sets a boolean indicating that this input can be edited in the Inspector (false by default) */
        this.visibleInInspector = false;
        /** Gets or sets a boolean indicating that this input can be edited from a collapsed frame */
        this.visibleOnFrame = false;
        this._target = target;
        this._originalTargetIsNeutral = target === NodeMaterialBlockTargets.Neutral;
        this._isFinalMerger = isFinalMerger;
        this._isInput = isInput;
        this._name = name;
        this.uniqueId = UniqueIdGenerator.UniqueId;
    }
    Object.defineProperty(NodeMaterialBlock.prototype, "name", {
        /**
         * Gets the name of the block
         */
        get: function () {
            return this._name;
        },
        /**
         * Sets the name of the block. Will check if the name is valid.
         */
        set: function (newName) {
            if (!this.validateBlockName(newName)) {
                return;
            }
            this._name = newName;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(NodeMaterialBlock.prototype, "isUnique", {
        /**
         * Gets a boolean indicating that this block can only be used once per NodeMaterial
         */
        get: function () {
            return this._isUnique;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(NodeMaterialBlock.prototype, "isFinalMerger", {
        /**
         * Gets a boolean indicating that this block is an end block (e.g. it is generating a system value)
         */
        get: function () {
            return this._isFinalMerger;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(NodeMaterialBlock.prototype, "isInput", {
        /**
         * Gets a boolean indicating that this block is an input (e.g. it sends data to the shader)
         */
        get: function () {
            return this._isInput;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(NodeMaterialBlock.prototype, "buildId", {
        /**
         * Gets or sets the build Id
         */
        get: function () {
            return this._buildId;
        },
        set: function (value) {
            this._buildId = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(NodeMaterialBlock.prototype, "target", {
        /**
         * Gets or sets the target of the block
         */
        get: function () {
            return this._target;
        },
        set: function (value) {
            if ((this._target & value) !== 0) {
                return;
            }
            this._target = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(NodeMaterialBlock.prototype, "inputs", {
        /**
         * Gets the list of input points
         */
        get: function () {
            return this._inputs;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(NodeMaterialBlock.prototype, "outputs", {
        /** Gets the list of output points */
        get: function () {
            return this._outputs;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Find an input by its name
     * @param name defines the name of the input to look for
     * @returns the input or null if not found
     */
    NodeMaterialBlock.prototype.getInputByName = function (name) {
        var filter = this._inputs.filter(function (e) { return e.name === name; });
        if (filter.length) {
            return filter[0];
        }
        return null;
    };
    /**
     * Find an output by its name
     * @param name defines the name of the output to look for
     * @returns the output or null if not found
     */
    NodeMaterialBlock.prototype.getOutputByName = function (name) {
        var filter = this._outputs.filter(function (e) { return e.name === name; });
        if (filter.length) {
            return filter[0];
        }
        return null;
    };
    /**
     * Initialize the block and prepare the context for build
     * @param state defines the state that will be used for the build
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.initialize = function (state) {
        // Do nothing
    };
    /**
     * Bind data to effect. Will only be called for blocks with isBindable === true
     * @param effect defines the effect to bind data to
     * @param nodeMaterial defines the hosting NodeMaterial
     * @param mesh defines the mesh that will be rendered
     * @param subMesh defines the submesh that will be rendered
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.bind = function (effect, nodeMaterial, mesh, subMesh) {
        // Do nothing
    };
    NodeMaterialBlock.prototype._declareOutput = function (output, state) {
        return "".concat(state._getGLType(output.type), " ").concat(output.associatedVariableName);
    };
    NodeMaterialBlock.prototype._writeVariable = function (currentPoint) {
        var connectionPoint = currentPoint.connectedPoint;
        if (connectionPoint) {
            return "".concat(currentPoint.associatedVariableName);
        }
        return "0.";
    };
    NodeMaterialBlock.prototype._writeFloat = function (value) {
        var stringVersion = value.toString();
        if (stringVersion.indexOf(".") === -1) {
            stringVersion += ".0";
        }
        return "".concat(stringVersion);
    };
    /**
     * Gets the current class name e.g. "NodeMaterialBlock"
     * @returns the class name
     */
    NodeMaterialBlock.prototype.getClassName = function () {
        return "NodeMaterialBlock";
    };
    /**
     * Register a new input. Must be called inside a block constructor
     * @param name defines the connection point name
     * @param type defines the connection point type
     * @param isOptional defines a boolean indicating that this input can be omitted
     * @param target defines the target to use to limit the connection point (will be VertexAndFragment by default)
     * @param point an already created connection point. If not provided, create a new one
     * @returns the current block
     */
    NodeMaterialBlock.prototype.registerInput = function (name, type, isOptional, target, point) {
        if (isOptional === void 0) { isOptional = false; }
        point = point !== null && point !== void 0 ? point : new NodeMaterialConnectionPoint(name, this, NodeMaterialConnectionPointDirection.Input);
        point.type = type;
        point.isOptional = isOptional;
        if (target) {
            point.target = target;
        }
        this._inputs.push(point);
        return this;
    };
    /**
     * Register a new output. Must be called inside a block constructor
     * @param name defines the connection point name
     * @param type defines the connection point type
     * @param target defines the target to use to limit the connection point (will be VertexAndFragment by default)
     * @param point an already created connection point. If not provided, create a new one
     * @returns the current block
     */
    NodeMaterialBlock.prototype.registerOutput = function (name, type, target, point) {
        point = point !== null && point !== void 0 ? point : new NodeMaterialConnectionPoint(name, this, NodeMaterialConnectionPointDirection.Output);
        point.type = type;
        if (target) {
            point.target = target;
        }
        this._outputs.push(point);
        return this;
    };
    /**
     * Will return the first available input e.g. the first one which is not an uniform or an attribute
     * @param forOutput defines an optional connection point to check compatibility with
     * @returns the first available input or null
     */
    NodeMaterialBlock.prototype.getFirstAvailableInput = function (forOutput) {
        if (forOutput === void 0) { forOutput = null; }
        for (var _i = 0, _a = this._inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            if (!input.connectedPoint) {
                if (!forOutput || forOutput.type === input.type || input.type === NodeMaterialBlockConnectionPointTypes.AutoDetect) {
                    return input;
                }
            }
        }
        return null;
    };
    /**
     * Will return the first available output e.g. the first one which is not yet connected and not a varying
     * @param forBlock defines an optional block to check compatibility with
     * @returns the first available input or null
     */
    NodeMaterialBlock.prototype.getFirstAvailableOutput = function (forBlock) {
        if (forBlock === void 0) { forBlock = null; }
        for (var _i = 0, _a = this._outputs; _i < _a.length; _i++) {
            var output = _a[_i];
            if (!forBlock || !forBlock.target || forBlock.target === NodeMaterialBlockTargets.Neutral || (forBlock.target & output.target) !== 0) {
                return output;
            }
        }
        return null;
    };
    /**
     * Gets the sibling of the given output
     * @param current defines the current output
     * @returns the next output in the list or null
     */
    NodeMaterialBlock.prototype.getSiblingOutput = function (current) {
        var index = this._outputs.indexOf(current);
        if (index === -1 || index >= this._outputs.length) {
            return null;
        }
        return this._outputs[index + 1];
    };
    /**
     * Checks if the current block is an ancestor of a given block
     * @param block defines the potential descendant block to check
     * @returns true if block is a descendant
     */
    NodeMaterialBlock.prototype.isAnAncestorOf = function (block) {
        for (var _i = 0, _a = this._outputs; _i < _a.length; _i++) {
            var output = _a[_i];
            if (!output.hasEndpoints) {
                continue;
            }
            for (var _b = 0, _c = output.endpoints; _b < _c.length; _b++) {
                var endpoint = _c[_b];
                if (endpoint.ownerBlock === block) {
                    return true;
                }
                if (endpoint.ownerBlock.isAnAncestorOf(block)) {
                    return true;
                }
            }
        }
        return false;
    };
    /**
     * Connect current block with another block
     * @param other defines the block to connect with
     * @param options define the various options to help pick the right connections
     * @param options.input
     * @param options.output
     * @param options.outputSwizzle
     * @returns the current block
     */
    NodeMaterialBlock.prototype.connectTo = function (other, options) {
        if (this._outputs.length === 0) {
            return;
        }
        var output = options && options.output ? this.getOutputByName(options.output) : this.getFirstAvailableOutput(other);
        var notFound = true;
        while (notFound) {
            var input = options && options.input ? other.getInputByName(options.input) : other.getFirstAvailableInput(output);
            if (output && input && output.canConnectTo(input)) {
                output.connectTo(input);
                notFound = false;
            }
            else if (!output) {
                throw "Unable to find a compatible match";
            }
            else {
                output = this.getSiblingOutput(output);
            }
        }
        return this;
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype._buildBlock = function (state) {
        // Empty. Must be defined by child nodes
    };
    /**
     * Add uniforms, samplers and uniform buffers at compilation time
     * @param state defines the state to update
     * @param nodeMaterial defines the node material requesting the update
     * @param defines defines the material defines to update
     * @param uniformBuffers defines the list of uniform buffer names
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.updateUniformsAndSamples = function (state, nodeMaterial, defines, uniformBuffers) {
        // Do nothing
    };
    /**
     * Add potential fallbacks if shader compilation fails
     * @param mesh defines the mesh to be rendered
     * @param fallbacks defines the current prioritized list of fallbacks
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.provideFallbacks = function (mesh, fallbacks) {
        // Do nothing
    };
    /**
     * Initialize defines for shader compilation
     * @param mesh defines the mesh to be rendered
     * @param nodeMaterial defines the node material requesting the update
     * @param defines defines the material defines to update
     * @param useInstances specifies that instances should be used
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.initializeDefines = function (mesh, nodeMaterial, defines, useInstances) {
        if (useInstances === void 0) { useInstances = false; }
    };
    /**
     * Update defines for shader compilation
     * @param mesh defines the mesh to be rendered
     * @param nodeMaterial defines the node material requesting the update
     * @param defines defines the material defines to update
     * @param useInstances specifies that instances should be used
     * @param subMesh defines which submesh to render
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.prepareDefines = function (mesh, nodeMaterial, defines, useInstances, subMesh) {
        if (useInstances === void 0) { useInstances = false; }
        // Do nothing
    };
    /**
     * Lets the block try to connect some inputs automatically
     * @param material defines the hosting NodeMaterial
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.autoConfigure = function (material) {
        // Do nothing
    };
    /**
     * Function called when a block is declared as repeatable content generator
     * @param vertexShaderState defines the current compilation state for the vertex shader
     * @param fragmentShaderState defines the current compilation state for the fragment shader
     * @param mesh defines the mesh to be rendered
     * @param defines defines the material defines to update
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.replaceRepeatableContent = function (vertexShaderState, fragmentShaderState, mesh, defines) {
        // Do nothing
    };
    Object.defineProperty(NodeMaterialBlock.prototype, "willBeGeneratedIntoVertexShaderFromFragmentShader", {
        /** Gets a boolean indicating that the code of this block will be promoted to vertex shader even if connected to fragment output */
        get: function () {
            if (this.isInput || this.isFinalMerger) {
                return false;
            }
            if (this._outputs.some(function (o) { return o.isDirectlyConnectedToVertexOutput; })) {
                return false;
            }
            if (this.target === NodeMaterialBlockTargets.Vertex) {
                return false;
            }
            if (this.target === NodeMaterialBlockTargets.VertexAndFragment || this.target === NodeMaterialBlockTargets.Neutral) {
                if (this._outputs.some(function (o) { return o.isConnectedInVertexShader; })) {
                    return true;
                }
            }
            return false;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Checks if the block is ready
     * @param mesh defines the mesh to be rendered
     * @param nodeMaterial defines the node material requesting the update
     * @param defines defines the material defines to update
     * @param useInstances specifies that instances should be used
     * @returns true if the block is ready
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype.isReady = function (mesh, nodeMaterial, defines, useInstances) {
        if (useInstances === void 0) { useInstances = false; }
        return true;
    };
    NodeMaterialBlock.prototype._linkConnectionTypes = function (inputIndex0, inputIndex1, looseCoupling) {
        if (looseCoupling === void 0) { looseCoupling = false; }
        if (looseCoupling) {
            this._inputs[inputIndex1]._acceptedConnectionPointType = this._inputs[inputIndex0];
        }
        else {
            this._inputs[inputIndex0]._linkedConnectionSource = this._inputs[inputIndex1];
        }
        this._inputs[inputIndex1]._linkedConnectionSource = this._inputs[inputIndex0];
    };
    NodeMaterialBlock.prototype._processBuild = function (block, state, input, activeBlocks) {
        block.build(state, activeBlocks);
        var localBlockIsFragment = state._vertexState != null;
        var otherBlockWasGeneratedInVertexShader = block._buildTarget === NodeMaterialBlockTargets.Vertex && block.target !== NodeMaterialBlockTargets.VertexAndFragment;
        if (localBlockIsFragment &&
            ((block.target & block._buildTarget) === 0 ||
                (block.target & input.target) === 0 ||
                (this.target !== NodeMaterialBlockTargets.VertexAndFragment && otherBlockWasGeneratedInVertexShader))) {
            // context switch! We need a varying
            if ((!block.isInput && state.target !== block._buildTarget) || // block was already emitted by vertex shader
                (block.isInput && block.isAttribute && !block._noContextSwitch) // block is an attribute
            ) {
                var connectedPoint = input.connectedPoint;
                if (state._vertexState._emitVaryingFromString("v_" + connectedPoint.associatedVariableName, state._getGLType(connectedPoint.type))) {
                    state._vertexState.compilationString += "".concat("v_" + connectedPoint.associatedVariableName, " = ").concat(connectedPoint.associatedVariableName, ";\r\n");
                }
                input.associatedVariableName = "v_" + connectedPoint.associatedVariableName;
                input._enforceAssociatedVariableName = true;
            }
        }
    };
    /**
     * Validates the new name for the block node.
     * @param newName the new name to be given to the node.
     * @returns false if the name is a reserve word, else true.
     */
    NodeMaterialBlock.prototype.validateBlockName = function (newName) {
        var reservedNames = [
            "position",
            "normal",
            "tangent",
            "particle_positionw",
            "uv",
            "uv2",
            "uv3",
            "uv4",
            "uv5",
            "uv6",
            "position2d",
            "particle_uv",
            "matricesIndices",
            "matricesWeights",
            "world0",
            "world1",
            "world2",
            "world3",
            "particle_color",
            "particle_texturemask",
        ];
        for (var _i = 0, reservedNames_1 = reservedNames; _i < reservedNames_1.length; _i++) {
            var reservedName = reservedNames_1[_i];
            if (newName === reservedName) {
                return false;
            }
        }
        return true;
    };
    /**
     * Compile the current node and generate the shader code
     * @param state defines the current compilation state (uniforms, samplers, current string)
     * @param activeBlocks defines the list of active blocks (i.e. blocks to compile)
     * @returns true if already built
     */
    NodeMaterialBlock.prototype.build = function (state, activeBlocks) {
        if (this._buildId === state.sharedData.buildId) {
            return true;
        }
        if (!this.isInput) {
            /** Prepare outputs */
            for (var _i = 0, _a = this._outputs; _i < _a.length; _i++) {
                var output = _a[_i];
                if (!output.associatedVariableName) {
                    output.associatedVariableName = state._getFreeVariableName(output.name);
                }
            }
        }
        // Check if "parent" blocks are compiled
        for (var _b = 0, _c = this._inputs; _b < _c.length; _b++) {
            var input = _c[_b];
            if (!input.connectedPoint) {
                if (!input.isOptional) {
                    // Emit a warning
                    state.sharedData.checks.notConnectedNonOptionalInputs.push(input);
                }
                continue;
            }
            if (this.target !== NodeMaterialBlockTargets.Neutral) {
                if ((input.target & this.target) === 0) {
                    continue;
                }
                if ((input.target & state.target) === 0) {
                    continue;
                }
            }
            var block = input.connectedPoint.ownerBlock;
            if (block && block !== this) {
                this._processBuild(block, state, input, activeBlocks);
            }
        }
        if (this._buildId === state.sharedData.buildId) {
            return true; // Need to check again as inputs can be connected multiple time to this endpoint
        }
        // Logs
        if (state.sharedData.verbose) {
            console.log("".concat(state.target === NodeMaterialBlockTargets.Vertex ? "Vertex shader" : "Fragment shader", ": Building ").concat(this.name, " [").concat(this.getClassName(), "]"));
        }
        // Checks final outputs
        if (this.isFinalMerger) {
            switch (state.target) {
                case NodeMaterialBlockTargets.Vertex:
                    state.sharedData.checks.emitVertex = true;
                    break;
                case NodeMaterialBlockTargets.Fragment:
                    state.sharedData.checks.emitFragment = true;
                    break;
            }
        }
        if (!this.isInput && state.sharedData.emitComments) {
            state.compilationString += "\r\n//".concat(this.name, "\r\n");
        }
        this._buildBlock(state);
        this._buildId = state.sharedData.buildId;
        this._buildTarget = state.target;
        // Compile connected blocks
        for (var _d = 0, _e = this._outputs; _d < _e.length; _d++) {
            var output = _e[_d];
            if ((output.target & state.target) === 0) {
                continue;
            }
            for (var _f = 0, _g = output.endpoints; _f < _g.length; _f++) {
                var endpoint = _g[_f];
                var block = endpoint.ownerBlock;
                if (block && (block.target & state.target) !== 0 && activeBlocks.indexOf(block) !== -1) {
                    this._processBuild(block, state, endpoint, activeBlocks);
                }
            }
        }
        return false;
    };
    NodeMaterialBlock.prototype._inputRename = function (name) {
        return name;
    };
    NodeMaterialBlock.prototype._outputRename = function (name) {
        return name;
    };
    NodeMaterialBlock.prototype._dumpPropertiesCode = function () {
        var variableName = this._codeVariableName;
        return "".concat(variableName, ".visibleInInspector = ").concat(this.visibleInInspector, ";\r\n").concat(variableName, ".visibleOnFrame = ").concat(this.visibleOnFrame, ";\r\n").concat(variableName, ".target = ").concat(this.target, ";\r\n");
    };
    /**
     * @param uniqueNames
     * @param alreadyDumped
     * @hidden
     */
    NodeMaterialBlock.prototype._dumpCode = function (uniqueNames, alreadyDumped) {
        alreadyDumped.push(this);
        var codeString;
        // Get unique name
        var nameAsVariableName = this.name.replace(/[^A-Za-z_]+/g, "");
        this._codeVariableName = nameAsVariableName || "".concat(this.getClassName(), "_").concat(this.uniqueId);
        if (uniqueNames.indexOf(this._codeVariableName) !== -1) {
            var index = 0;
            do {
                index++;
                this._codeVariableName = nameAsVariableName + index;
            } while (uniqueNames.indexOf(this._codeVariableName) !== -1);
        }
        uniqueNames.push(this._codeVariableName);
        // Declaration
        codeString = "\r\n// ".concat(this.getClassName(), "\r\n");
        if (this.comments) {
            codeString += "// ".concat(this.comments, "\r\n");
        }
        codeString += "var ".concat(this._codeVariableName, " = new BABYLON.").concat(this.getClassName(), "(\"").concat(this.name, "\");\r\n");
        // Properties
        codeString += this._dumpPropertiesCode();
        // Inputs
        for (var _i = 0, _a = this.inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            if (!input.isConnected) {
                continue;
            }
            var connectedOutput = input.connectedPoint;
            var connectedBlock = connectedOutput.ownerBlock;
            if (alreadyDumped.indexOf(connectedBlock) === -1) {
                codeString += connectedBlock._dumpCode(uniqueNames, alreadyDumped);
            }
        }
        // Outputs
        for (var _b = 0, _c = this.outputs; _b < _c.length; _b++) {
            var output = _c[_b];
            if (!output.hasEndpoints) {
                continue;
            }
            for (var _d = 0, _e = output.endpoints; _d < _e.length; _d++) {
                var endpoint = _e[_d];
                var connectedBlock = endpoint.ownerBlock;
                if (connectedBlock && alreadyDumped.indexOf(connectedBlock) === -1) {
                    codeString += connectedBlock._dumpCode(uniqueNames, alreadyDumped);
                }
            }
        }
        return codeString;
    };
    /**
     * @param alreadyDumped
     * @hidden
     */
    NodeMaterialBlock.prototype._dumpCodeForOutputConnections = function (alreadyDumped) {
        var codeString = "";
        if (alreadyDumped.indexOf(this) !== -1) {
            return codeString;
        }
        alreadyDumped.push(this);
        for (var _i = 0, _a = this.inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            if (!input.isConnected) {
                continue;
            }
            var connectedOutput = input.connectedPoint;
            var connectedBlock = connectedOutput.ownerBlock;
            codeString += connectedBlock._dumpCodeForOutputConnections(alreadyDumped);
            codeString += "".concat(connectedBlock._codeVariableName, ".").concat(connectedBlock._outputRename(connectedOutput.name), ".connectTo(").concat(this._codeVariableName, ".").concat(this._inputRename(input.name), ");\r\n");
        }
        return codeString;
    };
    /**
     * Clone the current block to a new identical block
     * @param scene defines the hosting scene
     * @param rootUrl defines the root URL to use to load textures and relative dependencies
     * @returns a copy of the current block
     */
    NodeMaterialBlock.prototype.clone = function (scene, rootUrl) {
        if (rootUrl === void 0) { rootUrl = ""; }
        var serializationObject = this.serialize();
        var blockType = GetClass(serializationObject.customType);
        if (blockType) {
            var block = new blockType();
            block._deserialize(serializationObject, scene, rootUrl);
            return block;
        }
        return null;
    };
    /**
     * Serializes this block in a JSON representation
     * @returns the serialized block object
     */
    NodeMaterialBlock.prototype.serialize = function () {
        var serializationObject = {};
        serializationObject.customType = "BABYLON." + this.getClassName();
        serializationObject.id = this.uniqueId;
        serializationObject.name = this.name;
        serializationObject.comments = this.comments;
        serializationObject.visibleInInspector = this.visibleInInspector;
        serializationObject.visibleOnFrame = this.visibleOnFrame;
        serializationObject.target = this.target;
        serializationObject.inputs = [];
        serializationObject.outputs = [];
        for (var _i = 0, _a = this.inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            serializationObject.inputs.push(input.serialize());
        }
        for (var _b = 0, _c = this.outputs; _b < _c.length; _b++) {
            var output = _c[_b];
            serializationObject.outputs.push(output.serialize(false));
        }
        return serializationObject;
    };
    /**
     * @param serializationObject
     * @param scene
     * @param rootUrl
     * @hidden
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NodeMaterialBlock.prototype._deserialize = function (serializationObject, scene, rootUrl) {
        var _a;
        this.name = serializationObject.name;
        this.comments = serializationObject.comments;
        this.visibleInInspector = !!serializationObject.visibleInInspector;
        this.visibleOnFrame = !!serializationObject.visibleOnFrame;
        this._target = (_a = serializationObject.target) !== null && _a !== void 0 ? _a : this.target;
        this._deserializePortDisplayNamesAndExposedOnFrame(serializationObject);
    };
    NodeMaterialBlock.prototype._deserializePortDisplayNamesAndExposedOnFrame = function (serializationObject) {
        var _this = this;
        var serializedInputs = serializationObject.inputs;
        var serializedOutputs = serializationObject.outputs;
        if (serializedInputs) {
            serializedInputs.forEach(function (port, i) {
                if (port.displayName) {
                    _this.inputs[i].displayName = port.displayName;
                }
                if (port.isExposedOnFrame) {
                    _this.inputs[i].isExposedOnFrame = port.isExposedOnFrame;
                    _this.inputs[i].exposedPortPosition = port.exposedPortPosition;
                }
            });
        }
        if (serializedOutputs) {
            serializedOutputs.forEach(function (port, i) {
                if (port.displayName) {
                    _this.outputs[i].displayName = port.displayName;
                }
                if (port.isExposedOnFrame) {
                    _this.outputs[i].isExposedOnFrame = port.isExposedOnFrame;
                    _this.outputs[i].exposedPortPosition = port.exposedPortPosition;
                }
            });
        }
    };
    /**
     * Release resources
     */
    NodeMaterialBlock.prototype.dispose = function () {
        for (var _i = 0, _a = this.inputs; _i < _a.length; _i++) {
            var input = _a[_i];
            input.dispose();
        }
        for (var _b = 0, _c = this.outputs; _b < _c.length; _b++) {
            var output = _c[_b];
            output.dispose();
        }
    };
    return NodeMaterialBlock;
}());
export { NodeMaterialBlock };
//# sourceMappingURL=nodeMaterialBlock.js.map